import type { HttpError } from '@refinedev/core'
import { useInvalidate, useNotification } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Game, GameConfiguration } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { formatDateTime } from '~/i18n/format'
import { PLATFORM_ACTIVITY_RESOURCE } from '~/providers/dataProvider'
import { errorMessage } from '~/providers/errors'
import { configurationPath } from './paths'
import { RtpValue } from './rtpValue'

/**
 * Makes a configuration the game's default
 * (`games.setDefaultConfiguration`), after a confirmation. On the
 * configuration list and on the configuration's page.
 */
export function SetDefaultAction({
  game,
  configuration,
}: {
  game: Game
  configuration: Pick<GameConfiguration, 'id' | 'name'>
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const invalidate = useInvalidate()
  const { open } = useNotification()
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      client.games.setDefaultConfiguration({
        gameId: game.id,
        configurationId: configuration.id,
      }),
    onSuccess: async () => {
      await Promise.all([
        // the list's Default badges and the configuration page's
        invalidate({
          resource: 'gameConfigurations',
          invalidates: ['list', 'detail'],
          id: configuration.id,
        }),
        // the game's History lists the new event
        invalidate({
          resource: PLATFORM_ACTIVITY_RESOURCE,
          invalidates: ['list'],
        }),
        invalidate({
          resource: 'games',
          invalidates: ['list', 'detail'],
          id: game.id,
        }),
      ])
      open?.({
        type: 'success',
        message: t('gamesPage.setDefault.success', {
          configuration: configuration.name,
          game: game.name,
        }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('gamesPage.setDefault.error'),
        description: errorMessage(error),
      })
    },
  })

  return (
    <ConfirmDialog
      trigger={
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
        >
          {t('gamesPage.setDefault.button')}
        </Button>
      }
      title={t('gamesPage.setDefault.confirmTitle', {
        configuration: configuration.name,
      })}
      description={t('gamesPage.setDefault.confirmDescription', {
        configuration: configuration.name,
        previous: game.defaultConfigurationName,
      })}
      confirmLabel={t('gamesPage.setDefault.button')}
      onConfirm={() => mutate()}
    />
  )
}

/**
 * A game's configurations, the platform's and every tenant's own, by name:
 * which one cabinets run (Default), which have a draft (Draft) and which
 * were never published (Not published), who each belongs to (Platform or
 * the tenant), the number and analytic RTP of each one's current published
 * version ("—" before its first publish), when and by whom each was
 * changed last, and "Set as default" (published platform configurations
 * only). The name opens the configuration's page (draft, editor, save
 * history, simulations, test seeds).
 */
export function GameConfigurationList({ game }: { game: Game }) {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<GameConfiguration>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        size: 220,
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('gamesPage.configurationList.columns.name')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-2 whitespace-normal">
            <Link
              to={configurationPath(game.id, row.original.id)}
              className="hover:text-primary font-medium underline underline-offset-4"
            >
              {row.original.name}
            </Link>
            {row.original.isDefault && (
              <Badge>{t('gamesPage.configurationList.badges.default')}</Badge>
            )}
            {row.original.draft !== null && (
              <Badge variant="outline">
                {t('gamesPage.configurationList.badges.draft')}
              </Badge>
            )}
            {row.original.currentConfigId === null && (
              <Badge variant="secondary">
                {t('gamesPage.configurationList.badges.notPublished')}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'owner',
        accessorKey: 'tenantName',
        header: t('gamesPage.configurationList.columns.owner'),
        size: 150,
        enableSorting: false,
        // tenant names are long: let them wrap
        cell: ({ row }) =>
          row.original.tenantName === null ? (
            <span className="text-muted-foreground">
              {t('gamesPage.configurationList.platform')}
            </span>
          ) : (
            <span className="whitespace-normal">{row.original.tenantName}</span>
          ),
      },
      {
        id: 'currentVersion',
        accessorKey: 'currentVersion',
        header: t('gamesPage.configurationList.columns.version'),
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.currentVersion === null
              ? '—'
              : t('gamesPage.configurationList.version', {
                  version: row.original.currentVersion,
                })}
          </span>
        ),
      },
      {
        id: 'analyticRtp',
        accessorKey: 'analyticRtp',
        header: t('gamesPage.configurationList.columns.analyticRtp'),
        size: 150,
        enableSorting: false,
        cell: ({ row }) => <RtpValue analytic={row.original.analyticRtp} />,
      },
      {
        id: 'updatedAt',
        size: 170,
        accessorKey: 'updatedAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('gamesPage.configurationList.columns.updated')}
            <DataTableSorter column={column} />
          </div>
        ),
        // the cells truncate by default: let the date, time and author wrap
        cell: ({ row }) => {
          const author = row.original.updatedBy
          return (
            <span className="whitespace-normal">
              {formatDateTime(row.original.updatedAt)}
              {author !== null && (
                <span
                  className="text-muted-foreground"
                  title={author.email}
                >
                  {' '}
                  {t('gamesPage.configurationList.updatedBy', {
                    name: author.name,
                  })}
                </span>
              )}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            {!row.original.isDefault &&
              row.original.tenantId === null &&
              row.original.currentConfigId !== null && (
                <SetDefaultAction
                  game={game}
                  configuration={row.original}
                />
              )}
          </div>
        ),
      },
    ],
    [game, t],
  )

  const table = useTable<GameConfiguration, HttpError>({
    columns,
    enableColumnFilters: false,
    refineCoreProps: {
      resource: 'gameConfigurations',
      // the show page's URL belongs to the game, not to this table
      syncWithLocation: false,
      pagination: { pageSize: 10 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
      filters: {
        permanent: [{ field: 'gameId', operator: 'eq', value: game.id }],
      },
    },
  })

  return <DataTable table={table} />
}
